const API_URL = import.meta.env.VITE_API_URL || '';

// Types
export interface User {
  id: string;
  pin?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  is_admin: boolean;
  assigned_store_id: string | null;
  territory_ids: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

export interface RoleVisit {
  date: string;
  score: number;
}

export interface StoreCases {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

export interface StoreScore {
  score: number;
  as_of: string;
}

export interface Store {
  id: string;
  store_number: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  district: string;
  market: string;
  region: string;
  status: string;
  created_at: string;
  updated_at: string;
  // Extended metrics
  last_visits?: {
    SM: RoleVisit | null;
    DM: RoleVisit | null;
    MM: RoleVisit | null;
    LP: RoleVisit | null;
    BUYER: RoleVisit | null;
    ELT: RoleVisit | null;
    VM: RoleVisit | null;
  };
  open_cases?: StoreCases;
  latest_score?: StoreScore | null;
}

export interface QuestionGroup {
  id: string;
  name: string;
  display_order?: number;
  status?: string;
  parent_id?: string;
  questions?: Question[];
  children?: QuestionGroup[];
  totalQuestions?: number;
}

export interface Question {
  id: string;
  group_id: string;
  text: string;
  question_text?: string;
  display_order: number;
  severity?: string;
  description?: string;
  role?: string;
  status?: string;
}

export interface CasePhoto {
  id: string;
  answer_id: string;
  photo_url: string;
  annotated_photo_url?: string;
  created_at: string;
}

export interface CaseNote {
  id: string;
  case_id: string;
  user_id: string;
  user_name: string;
  note: string;
  created_at: string;
}

export interface Case {
  id: string;
  visit_id: string;
  visit_answer_id: string;
  store_id: string;
  question_id: string;
  assigned_to_user_id: string;
  reported_by_user_id: string;
  severity: string;
  status: string;
  deadline_at: string;
  notes?: string;
  resolution_comment?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  store_name?: string;
  store_number?: string;
  question_text?: string;
  assignee_name?: string;
  reporter_name?: string;
  days_remaining?: number;
  is_overdue?: boolean;
  photos?: CasePhoto[];
}

export interface Visit {
  id: string;
  store_id: string;
  user_id: string;
  started_at: string;
  completed_at: string;
  overall_score: number;
  status: string;
  store_name?: string;
  store_number?: string;
  user_name?: string;
}

export interface VisitDetail extends Visit {
  answers: VisitAnswer[];
  scores: VisitScore[];
}

export interface VisitAnswer {
  id: string;
  visit_id: string;
  question_id: string;
  passed: number;
  severity: string;
  comment: string;
  created_at: string;
  question_text: string;
  group_name: string;
  photos: VisitPhoto[];
}

export interface VisitPhoto {
  id: string;
  answer_id: string;
  photo_url: string;
  annotated_photo_url: string;
  created_at: string;
}

export interface VisitScore {
  id: string;
  visit_id: string;
  store_id: string;
  group_id: string | null;
  group_name?: string;
  total_questions: number;
  passed_questions: number;
  score_percentage: number;
}

export interface CompanyReport {
  total_stores: number;
  total_visits_this_month: number;
  average_score: number;
  open_cases: {
    critical: number;
    moderate: number;
    minor: number;
    total: number;
    overdue: number;
  };
  districts: string[];
  markets: string[];
  score_trend: Array<{ date: string; score: number }>;
}

function getAuthHeaders(): HeadersInit {
  const stored = localStorage.getItem('fit2run-admin-auth');
  if (stored) {
    const { state } = JSON.parse(stored);
    if (state?.token) {
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${state.token}`,
      };
    }
  }
  return { 'Content-Type': 'application/json' };
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error?.message || 'Request failed');
  }
  return data.data;
}

// Users API
export const usersApi = {
  async list(): Promise<User[]> {
    const response = await fetch(`${API_URL}/api/users`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<User[]>(response);
  },

  async get(id: string): Promise<User> {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<User>(response);
  },

  async create(user: Partial<User> & { pin: string }): Promise<User> {
    const response = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(user),
    });
    return handleResponse<User>(response);
  },

  async update(id: string, user: Partial<User>): Promise<User> {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(user),
    });
    return handleResponse<User>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse<void>(response);
  },
};

// Stores API
export const storesApi = {
  async list(): Promise<Store[]> {
    const response = await fetch(`${API_URL}/api/stores`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Store[]>(response);
  },

  async get(id: string): Promise<Store> {
    const response = await fetch(`${API_URL}/api/stores/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Store>(response);
  },

  async create(store: Partial<Store>): Promise<Store> {
    const response = await fetch(`${API_URL}/api/stores`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(store),
    });
    return handleResponse<Store>(response);
  },

  async update(id: string, store: Partial<Store>): Promise<Store> {
    const response = await fetch(`${API_URL}/api/stores/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(store),
    });
    return handleResponse<Store>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/stores/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse<void>(response);
  },
};

// Questions API
export const questionsApi = {
  async listGroups(): Promise<QuestionGroup[]> {
    const response = await fetch(`${API_URL}/api/questions/groups`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<QuestionGroup[]>(response);
  },

  async listHierarchicalGroups(): Promise<QuestionGroup[]> {
    const response = await fetch(`${API_URL}/api/questions`, {
      headers: getAuthHeaders(),
    });
    // Backend returns hierarchical data with parent/child groups
    const groups = await handleResponse<QuestionGroup[]>(response);

    console.log('Raw API response:', JSON.stringify(groups, null, 2));

    // Normalize question text field for each group and its children
    const normalizeQuestions = (questions?: Question[]): Question[] => {
      if (!questions) return [];
      return questions.map(q => ({
        ...q,
        text: q.question_text || q.text || '',
        severity: q.severity || 'medium',
        description: q.description || '',
        role: q.role || '',
      }));
    };

    const result = groups.map(group => ({
      ...group,
      questions: normalizeQuestions(group.questions),
      children: group.children?.map(child => ({
        ...child,
        questions: normalizeQuestions(child.questions),
      })),
    }));

    console.log('Processed groups:', result.map(g => ({ name: g.name, totalQuestions: g.totalQuestions, childrenCount: g.children?.length })));

    return result;
  },

  async listQuestions(): Promise<Question[]> {
    const response = await fetch(`${API_URL}/api/questions`, {
      headers: getAuthHeaders(),
    });
    // Backend returns grouped data, extract flat question list
    // Backend uses question_text, frontend uses text
    const groups = await handleResponse<any[]>(response);
    const questions: Question[] = [];

    const extractQuestions = (group: any) => {
      if (group.questions) {
        for (const q of group.questions) {
          questions.push({
            ...q,
            text: q.question_text || q.text || '',
            severity: q.severity || 'medium',
            description: q.description || '',
            role: q.role || '',
          });
        }
      }
      // Also extract from children
      if (group.children) {
        for (const child of group.children) {
          extractQuestions(child);
        }
      }
    };

    for (const group of groups) {
      extractQuestions(group);
    }
    return questions;
  },

  async createGroup(group: Partial<QuestionGroup>): Promise<QuestionGroup> {
    const response = await fetch(`${API_URL}/api/questions/groups`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(group),
    });
    return handleResponse<QuestionGroup>(response);
  },

  async updateGroup(id: string, group: Partial<QuestionGroup>): Promise<QuestionGroup> {
    const response = await fetch(`${API_URL}/api/questions/groups/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(group),
    });
    return handleResponse<QuestionGroup>(response);
  },

  async createQuestion(question: Partial<Question>): Promise<Question> {
    // Map frontend field names to backend expected names
    const payload = {
      group_id: question.group_id,
      question_text: question.text, // Backend expects question_text, not text
      display_order: question.display_order,
      severity: question.severity,
      description: question.description,
      role: question.role,
    };
    console.log('createQuestion payload:', payload);
    const response = await fetch(`${API_URL}/api/questions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse<Question>(response);
  },

  async updateQuestion(id: string, question: Partial<Question>): Promise<Question> {
    // Map frontend field names to backend expected names
    const payload = {
      group_id: question.group_id,
      question_text: question.text, // Backend expects question_text, not text
      display_order: question.display_order,
      severity: question.severity,
      description: question.description,
      role: question.role,
    };
    const response = await fetch(`${API_URL}/api/questions/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse<Question>(response);
  },

  async deleteQuestion(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/questions/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse<void>(response);
  },
};

// Cases API
export const casesApi = {
  async list(): Promise<Case[]> {
    const response = await fetch(`${API_URL}/api/cases`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Case[]>(response);
  },

  async get(id: string): Promise<Case> {
    const response = await fetch(`${API_URL}/api/cases/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Case>(response);
  },

  async resolve(id: string, notes: string): Promise<Case> {
    const response = await fetch(`${API_URL}/api/cases/${id}/resolve`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ resolution_notes: notes }),
    });
    return handleResponse<Case>(response);
  },

  async updateStatus(id: string, status: string): Promise<Case> {
    const response = await fetch(`${API_URL}/api/cases/${id}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return handleResponse<Case>(response);
  },

  async addNote(id: string, notes: string): Promise<CaseNote> {
    const response = await fetch(`${API_URL}/api/cases/${id}/notes`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ notes }),
    });
    return handleResponse<CaseNote>(response);
  },

  async getNotes(id: string): Promise<CaseNote[]> {
    const response = await fetch(`${API_URL}/api/cases/${id}/notes`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<CaseNote[]>(response);
  },
};

// Reports API
export const reportsApi = {
  async getCompanyReport(): Promise<CompanyReport> {
    const response = await fetch(`${API_URL}/api/reports/company`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<CompanyReport>(response);
  },

  async getStoreReport(storeId: string) {
    const response = await fetch(`${API_URL}/api/reports/store/${storeId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
};

// Visits API
export const visitsApi = {
  async list(params?: { page?: number; per_page?: number; status?: string; store_id?: string }): Promise<{ data: Visit[]; meta: { page: number; per_page: number; total: number } }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.store_id) searchParams.set('store_id', params.store_id);
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await fetch(`${API_URL}/api/visits${query}`, {
      headers: getAuthHeaders(),
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Request failed');
    }
    return { data: result.data, meta: result.meta };
  },

  async get(id: string): Promise<VisitDetail> {
    const response = await fetch(`${API_URL}/api/visits/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VisitDetail>(response);
  },

  async getByStore(storeId: string, params?: { page?: number; per_page?: number }): Promise<Visit[]> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString());
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await fetch(`${API_URL}/api/visits/store/${storeId}${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Visit[]>(response);
  },
};
